/**
 * ToastService
 *
 * A comprehensive toast notification service that manages
 * success, warning, error, and info notifications with
 * queuing, custom durations, and dismissal capabilities.
 */

import {
  ToastMessage,
  ToastType,
  ToastOptions,
  ToastQueueState,
  ToastQueueConfig,
  ToastEvent,
  DEFAULT_TOAST_CONFIG,
  DEFAULT_QUEUE_CONFIG
} from '../../Models/Toast/ToastModels';

/**
 * Toast notification service class
 */
class ToastService {
  private static instance: ToastService;
  private state: ToastQueueState;
  private eventListeners: ((event: ToastEvent) => void)[] = [];
  private timeouts: Map<string, NodeJS.Timeout> = new Map();

  constructor() {
    this.state = {
      visible: [],
      queued: [],
      config: { ...DEFAULT_QUEUE_CONFIG }
    };
  }

  /**
   * Get singleton instance
   */
  static getInstance(): ToastService {
    if (!ToastService.instance) {
      ToastService.instance = new ToastService();
    }
    return ToastService.instance;
  }

  /**
   * Configure the toast queue settings
   */
  configure(config: Partial<ToastQueueConfig>): void {
    this.state.config = { ...this.state.config, ...config };
  }

  /**
   * Add an event listener for toast events
   */
  addEventListener(callback: (event: ToastEvent) => void): () => void {
    this.eventListeners.push(callback);
    return () => {
      const index = this.eventListeners.indexOf(callback);
      if (index > -1) {
        this.eventListeners.splice(index, 1);
      }
    };
  }

  /**
   * Emit an event to all listeners
   */
  private emit(event: ToastEvent): void {
    this.eventListeners.forEach(callback => callback(event));
  }

  /**
   * Generate a unique ID for a toast
   */
  private generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Create a toast message object
   */
  private createToast(type: ToastType, message: string, options: ToastOptions = {}): ToastMessage {
    const config = { ...DEFAULT_TOAST_CONFIG[type], ...options };

    // Validate and set duration
    let duration = config.duration;
    if (duration !== undefined && (duration < 0 || !Number.isFinite(duration))) {
      duration = DEFAULT_TOAST_CONFIG[type].duration || 2000;
    }

    return {
      id: this.generateId(),
      type,
      message,
      description: options.description,
      duration,
      dismissible: config.dismissible !== false,
      actionText: options.actionText,
      onAction: options.onAction,
      timestamp: Date.now()
    };
  }

  /**
   * Show a toast notification
   */
  show(type: ToastType, message: string, options: ToastOptions = {}): string {
    const toast = this.createToast(type, message, options);

    // Check if we can show immediately or need to queue
    if (this.state.visible.length < this.state.config.maxVisible) {
      this.showToast(toast);
    } else {
      this.queueToast(toast);
    }

    this.emit({ type: 'add', toast });
    return toast.id;
  }

  /**
   * Show a success toast
   */
  success(message: string, options: ToastOptions = {}): string {
    return this.show('success', message, options);
  }

  /**
   * Show a warning toast
   */
  warning(message: string, options: ToastOptions = {}): string {
    return this.show('warning', message, options);
  }

  /**
   * Show an error toast
   */
  error(message: string, options: ToastOptions = {}): string {
    return this.show('error', message, options);
  }

  /**
   * Show an info toast
   */
  info(message: string, options: ToastOptions = {}): string {
    return this.show('info', message, options);
  }

  /**
   * Add a toast to the visible list and set up auto-dismiss
   */
  private showToast(toast: ToastMessage): void {
    this.state.visible.push(toast);

    // Set up auto-dismiss timer if duration > 0
    if (toast.duration && toast.duration > 0) {
      const timeout = setTimeout(() => {
        this.dismiss(toast.id);
      }, toast.duration);
      this.timeouts.set(toast.id, timeout);
    }
  }

  /**
   * Add a toast to the queue
   */
  private queueToast(toast: ToastMessage): void {
    // Check queue size limit
    if (this.state.queued.length >= this.state.config.maxQueueSize) {
      // Remove oldest queued toast
      this.state.queued.shift();
    }
    this.state.queued.push(toast);
  }

  /**
   * Process the queue and show next toast if possible
   */
  private processQueue(): void {
    if (this.state.queued.length > 0 && this.state.visible.length < this.state.config.maxVisible) {
      const nextToast = this.state.queued.shift();
      if (nextToast) {
        this.showToast(nextToast);
      }
    }
  }

  /**
   * Dismiss a specific toast by ID
   */
  dismiss(toastId: string): void {
    // Clear timeout if exists
    const timeout = this.timeouts.get(toastId);
    if (timeout) {
      clearTimeout(timeout);
      this.timeouts.delete(toastId);
    }

    // Remove from visible toasts
    const visibleIndex = this.state.visible.findIndex(t => t.id === toastId);
    if (visibleIndex > -1) {
      const removedToast = this.state.visible.splice(visibleIndex, 1)[0];
      this.emit({ type: 'remove', toast: removedToast });

      // Process queue to show next toast
      this.processQueue();
    }
  }

  /**
   * Dismiss all visible toasts
   */
  dismissAll(): void {
    // Clear all timeouts
    this.timeouts.forEach(timeout => clearTimeout(timeout));
    this.timeouts.clear();

    // Clear visible toasts
    const removedToasts = [...this.state.visible];
    this.state.visible = [];

    // Emit remove events for each dismissed toast
    removedToasts.forEach(toast => {
      this.emit({ type: 'remove', toast });
    });
  }

  /**
   * Clear the entire queue (both visible and queued toasts)
   */
  clear(): void {
    // Clear all timeouts
    this.timeouts.forEach(timeout => clearTimeout(timeout));
    this.timeouts.clear();

    // Clear all toasts
    const allToasts = [...this.state.visible, ...this.state.queued];
    this.state.visible = [];
    this.state.queued = [];

    // Emit clear event
    this.emit({ type: 'clear' });

    // Emit individual remove events for tracking
    allToasts.forEach(toast => {
      this.emit({ type: 'remove', toast });
    });
  }

  /**
   * Get current queue state
   */
  getState(): ToastQueueState {
    return {
      visible: [...this.state.visible],
      queued: [...this.state.queued],
      config: { ...this.state.config }
    };
  }

  /**
   * Get visible toasts
   */
  getVisible(): ToastMessage[] {
    return [...this.state.visible];
  }

  /**
   * Get queued toasts
   */
  getQueued(): ToastMessage[] {
    return [...this.state.queued];
  }

  /**
   * Check if a toast with given ID exists
   */
  hasToast(toastId: string): boolean {
    return this.state.visible.some(t => t.id === toastId) ||
           this.state.queued.some(t => t.id === toastId);
  }

  /**
   * Update toast configuration
   */
  updateToast(toastId: string, updates: Partial<ToastMessage>): boolean {
    const visibleIndex = this.state.visible.findIndex(t => t.id === toastId);
    if (visibleIndex > -1) {
      this.state.visible[visibleIndex] = { ...this.state.visible[visibleIndex], ...updates };
      this.emit({ type: 'update', toast: this.state.visible[visibleIndex] });
      return true;
    }

    const queuedIndex = this.state.queued.findIndex(t => t.id === toastId);
    if (queuedIndex > -1) {
      this.state.queued[queuedIndex] = { ...this.state.queued[queuedIndex], ...updates };
      this.emit({ type: 'update', toast: this.state.queued[queuedIndex] });
      return true;
    }

    return false;
  }
}

// Export singleton instance
const toastService = ToastService.getInstance();

export default toastService;

// Export class for testing purposes
export { ToastService };